export type RepeatMode = "hourly" | "daily" | "weekly" | "monthly" | "custom";

export const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;
// Cron day-of-week convention: 1=Mon..7=Sun (some engines accept 0=Sun).
export const WEEKDAY_CRON_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;
export const DEFAULT_WEEKDAYS = [true, true, true, true, true, false, false];

export interface ParsedHeartbeatRecurrence {
  repeatMode: RepeatMode;
  hhmm: string;
  weekdays: boolean[];
  monthDay: number;
  customCron: string;
  hourlyStartDate: string;
  hourlyStartTime: string;
}

interface BuildCronOptions {
  repeatMode: RepeatMode;
  hhmm: string;
  weekdays: boolean[];
  monthDay: number;
  customCron: string;
  hourlyStartTime: string;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export function parseHhmm(s: string): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!match) return { h: 9, m: 0 };
  const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  const m = Math.min(59, Math.max(0, parseInt(match[2], 10)));
  return { h, m };
}

export function toLocalDateInput(value?: string | Date | null): string {
  const date =
    value instanceof Date ? value : value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    const fallback = new Date();
    return `${fallback.getFullYear()}-${pad2(fallback.getMonth() + 1)}-${pad2(fallback.getDate())}`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function toLocalTimeInput(value?: string | Date | null): string {
  const date =
    value instanceof Date ? value : value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "09:00";
  }
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function buildHeartbeatCron({
  repeatMode,
  hhmm,
  weekdays,
  monthDay,
  customCron,
  hourlyStartTime,
}: BuildCronOptions): string {
  const { h, m } = parseHhmm(hhmm);
  switch (repeatMode) {
    case "hourly":
      return `${parseHhmm(hourlyStartTime).m} * * * *`;
    case "daily":
      return `${m} ${h} * * *`;
    case "weekly": {
      const selected = weekdays
        .map((on, i) => (on ? WEEKDAY_CRON_VALUES[i] : null))
        .filter(
          (value): value is (typeof WEEKDAY_CRON_VALUES)[number] =>
            value !== null,
        );
      const dow = selected.length === 0 ? "*" : selected.join(",");
      return `${m} ${h} * * ${dow}`;
    }
    case "monthly":
      return `${m} ${h} ${Math.min(28, Math.max(1, monthDay))} * *`;
    case "custom":
    default:
      return customCron.trim();
  }
}

function parseNumericField(field: string): number | null {
  return /^\d+$/.test(field) ? parseInt(field, 10) : null;
}

export function parseHeartbeatCron(
  cron: string,
  referenceIso?: string | null,
): ParsedHeartbeatRecurrence {
  const trimmed = cron.trim();
  const reference = referenceIso ? new Date(referenceIso) : new Date();
  const hourlyStartDate = toLocalDateInput(reference);
  const hourlyStartTime = toLocalTimeInput(reference);
  const fallback: ParsedHeartbeatRecurrence = {
    repeatMode: "custom",
    hhmm: "09:00",
    weekdays: [...DEFAULT_WEEKDAYS],
    monthDay: 1,
    customCron: trimmed,
    hourlyStartDate,
    hourlyStartTime,
  };

  const fields = trimmed.split(/\s+/);
  if (fields.length !== 5) {
    return fallback;
  }

  const [minuteField, hourField, domField, monthField, dowField] = fields;
  const minute = parseNumericField(minuteField);
  const hour = parseNumericField(hourField);
  if (minute === null) {
    return fallback;
  }

  if (
    hourField === "*" &&
    domField === "*" &&
    monthField === "*" &&
    dowField === "*"
  ) {
    return {
      ...fallback,
      repeatMode: "hourly",
      customCron: trimmed,
      hourlyStartTime: `${hourlyStartTime.slice(0, 3)}${pad2(minute)}`,
    };
  }

  if (
    hour !== null &&
    domField === "*" &&
    monthField === "*" &&
    dowField === "*"
  ) {
    return {
      ...fallback,
      repeatMode: "daily",
      hhmm: `${pad2(hour)}:${pad2(minute)}`,
    };
  }

  if (
    hour !== null &&
    domField === "*" &&
    monthField === "*" &&
    dowField !== "*"
  ) {
    const tokens = dowField.split(",");
    const weekdays = Array.from({ length: 7 }, () => false);
    for (const token of tokens) {
      const day = parseNumericField(token);
      if (day === null) {
        return fallback;
      }
      const normalized = day === 0 ? 6 : day - 1;
      if (normalized < 0 || normalized > 6) {
        return fallback;
      }
      weekdays[normalized] = true;
    }
    return {
      ...fallback,
      repeatMode: "weekly",
      hhmm: `${pad2(hour)}:${pad2(minute)}`,
      weekdays,
    };
  }

  if (hour !== null && monthField === "*" && dowField === "*") {
    const monthDay = parseNumericField(domField);
    if (monthDay !== null) {
      return {
        ...fallback,
        repeatMode: "monthly",
        hhmm: `${pad2(hour)}:${pad2(minute)}`,
        monthDay: Math.min(28, Math.max(1, monthDay)),
      };
    }
  }

  return fallback;
}

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function heartbeatDebugEnabled(
  search = window.location.search,
): boolean {
  return new URLSearchParams(search).get("debug") === "1";
}
